import translate from '@translate';
import React, { FC } from 'react';
import { withArchiveMode } from 'src/utils/archiveMode';
import Grid, { GridItem } from 'src/components/Grid';
import Select from 'src/components/Select';
import TextField from 'src/components/TextField';
import { OrderType } from 'src/types/order';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import { ProviderType } from 'src/types/provider';
import DateTimePicker from 'src/components/DateTimePicker';
import { Typography } from '@mui/material';
import { orderTypeCodes } from 'src/resources/constantsCodes/constants';
import AutocompleteTextField from 'src/components/AutocompleteTextField';
import colorOptionFce from 'src/utils/colorOptionFce';
import Tooltip from '../../components/Tooltip';
import { InfoIcon } from '../../components/SkladonIcon/Icons';
import { styled } from '@mui/material/styles';
import RequiredExpeditionDateField from './RequiredExpeditionDateField';
import { sizes } from '../../themes/default';
import { FileCountryIcon, OrderIcon } from '../../components/SkladonIcon/SmallIcons';
import IconButton from '../../components/IconButton';
import { FormErrorsType, OnChangeType } from '../../types/global';
import { EditKeysType } from './constants';
import Switch from 'src/components/Switch';

export interface DetailPropsType {
    data: OrderType | null;
    onChange: OnChangeType<EditKeysType>;
    editData: OrderType;
    editErrors: FormErrorsType;
    disableEdit: boolean;
    permissionEditAll: boolean;
    permissionManagerEdit: boolean;
    isAttributeEditDisabled: (attributeKey: string) => boolean;
    orderStatusCodes: ConstantsCodeType[];
    allowedStatuses: ConstantsCodeType[];
    orderCancelReasonCodes: ConstantsCodeType[];
    orderMarketplacesCodes: ConstantsCodeType[];
    newOrder: boolean;
    summary?: boolean;
    incorrectMessage: string;
    skladonUser?: number;
    providerData?: ProviderType | null;
}

const InfoIconStyled = styled('span')({
    marginLeft: '10px',

    '& svg': {
        width: '14px',
        height: '14px',
        marginRight: '10px'
    }
});

const Detail: FC<DetailPropsType> = (props) => {
    function navigateToOrderDetail(orderId: number | string) {
        // Implement your navigation logic here, for example:
        window.location.href = withArchiveMode(`/orders/${orderId}`);
    }
    const statusOptions = React.useMemo(() => {
        if (props.newOrder) {
            return (props.orderStatusCodes || []).filter((opt) => ['preReceived', 'preOrder'].includes(String(opt.value)));
        }
        return props.orderStatusCodes;
    }, [props.newOrder, props.orderStatusCodes]);
    React.useEffect(() => {
        if (props.newOrder && !props.editData.STATUS) {
            // Default status for new order
            props.onChange('STATUS', 'preReceived', undefined as any);
        }
    }, [props.newOrder, props.editData.STATUS]);

    return (
        <Grid>
            <GridItem cols={12}>
                <Typography variant="h3" gutterBottom>
                    {translate('orderDetail.detail')}
                </Typography>
            </GridItem>
            <GridItem cols={12}>
                <Grid>
                    <GridItem cols={6}>
                        <Grid>
                            <GridItem cols={12}>
                                <TextField
                                    value={props.editData.ESHOP_ORDER_ID}
                                    error={props.editErrors.ESHOP_ORDER_ID}
                                    onChange={(value, error) => props.onChange('ESHOP_ORDER_ID', value, error)}
                                    name="ESHOP_ORDER_ID"
                                    label={translate('orderDetail.eshopID')}
                                    fullWidth
                                    disabled={!props.newOrder || props.summary}
                                />
                            </GridItem>
                            {props.editData.PARENT_ORDER_ID && (
                                <GridItem cols={12}>
                                    <TextField
                                        value={`${props.editData.PARENT_ORDER_ID} / ${props.editData.PARENT_ESHOP_ORDER_ID}`}
                                        error={props.editErrors.ESHOP_ORDER_ID}
                                        name="PARENT_ESHOP_ORDER_IDS"
                                        label={translate('orderDetail.parentIdEshopId')}
                                        disabled={true}
                                        sx={{ width: `calc(100% - ${46}px)` }}
                                    />
                                    <Tooltip title={translate('orderDetail.shipperLabel')}>
                                        <IconButton
                                            size="small"
                                            onClick={() => navigateToOrderDetail(props.editData.PARENT_ORDER_ID!)}
                                            sx={{
                                                margin: sizes.icon_button_next_to_input_MG,
                                                '& svg': {
                                                    width: sizes.icon_button_next_to_input_svg_size,
                                                    height: sizes.icon_button_next_to_input_svg_size
                                                }
                                            }}
                                        >
                                            <OrderIcon />
                                        </IconButton>
                                    </Tooltip>
                                </GridItem>
                            )}
                            <GridItem cols={12}>
                                <Select
                                    options={orderTypeCodes}
                                    value={props.editData.ORDER_TYPE}
                                    error={props.editErrors.ORDER_TYPE}
                                    onChange={(value, error) => props.onChange('ORDER_TYPE', value, error)}
                                    name="ORDER_TYPE"
                                    label={translate('orderDetail.orderType')}
                                    fullWidth
                                    required
                                    disabled={props.isAttributeEditDisabled('ORDER_TYPE') || props.summary}
                                />
                            </GridItem>
                            <GridItem cols={12}>
                                <Select
                                    options={statusOptions}
                                    editableOptions={!props.newOrder ? props.allowedStatuses : undefined}
                                    value={props.editData.STATUS}
                                    error={props.editErrors.STATUS}
                                    onChange={(value, error) => props.onChange('STATUS', value, error)}
                                    name="STATUS"
                                    label={translate('orderDetail.orderStatus')}
                                    fullWidth
                                    disabled={props.isAttributeEditDisabled('STATUS') || props.summary}
                                    required
                                    optionStyleFce={colorOptionFce}
                                />
                            </GridItem>
                            {((['inProcess', 'packed', 'waitingForCarrier'].includes(props.data?.STATUS || '') && props.editData?.STATUS === 'canceled') || props.data?.CANCEL_REASON) && props.permissionEditAll && (
                                <GridItem cols={12}>
                                    <Select
                                        options={props.orderCancelReasonCodes}
                                        value={props.editData.CANCEL_REASON === null ? '' : props.editData.CANCEL_REASON}
                                        error={props.editErrors.CANCEL_REASON}
                                        onChange={(value, error) => props.onChange('CANCEL_REASON', value, error)}
                                        name="CANCEL_REASON"
                                        label={translate('orderDetail.cancelReason')}
                                        fullWidth
                                        required
                                        disabled={props.disableEdit || !props.permissionEditAll}
                                    />
                                </GridItem>
                            )}
                            {props.editData?.STATUS === 'suspended' && (
                                <GridItem cols={12}>
                                    <DateTimePicker
                                        value={props.editData.RELEASE_FROM_SUSPENDED_AT}
                                        error={props.editErrors.RELEASE_FROM_SUSPENDED_AT}
                                        onChange={(value, error) =>
                                            props.onChange('RELEASE_FROM_SUSPENDED_AT', value, error)
                                        }
                                        name="RELEASE_FROM_SUSPENDED_AT"
                                        label={translate('orderDetail.releaseFromSuspendedAt')}
                                        fullWidth
                                        // format={'lll'}
                                        disabled={props.allowedStatuses.length < 2}
                                        disablePast
                                        type="dateTime"
                                    />
                                </GridItem>
                            )}
                            {props.editData?.STATUS === 'preOrder' && (
                                <GridItem cols={12}>
                                    <DateTimePicker
                                        value={props.editData.PREORDER_RELEASE_AT}
                                        error={props.editErrors.PREORDER_RELEASE_AT}
                                        onChange={(value, error) =>
                                            props.onChange('PREORDER_RELEASE_AT', value, error)
                                        }
                                        name="PREORDER_RELEASE_AT"
                                        label={translate('orderDetail.preorderReleaseAt')}
                                        fullWidth
                                        // format={'lll'}
                                        disabled={props.allowedStatuses.length < 2}
                                        disablePast
                                        type="dateTime"
                                    />
                                </GridItem>
                            )}
                            <GridItem cols={12}>
                                <Select
                                    options={props.orderMarketplacesCodes}
                                    value={props.editData.MARKETPLACE === null ? '' : props.editData.MARKETPLACE}
                                    error={props.editErrors.MARKETPLACE}
                                    onChange={(value, error) => props.onChange('MARKETPLACE', value === '' ? null : value, error)}
                                    name="MARKETPLACE"
                                    label={translate('orderDetail.marketplace')}
                                    fullWidth
                                    disabled={
                                        (!props.permissionEditAll && !props.permissionManagerEdit) || props.summary
                                    } />
                            </GridItem>
                            {props.editData.ORDER_TYPE?.toLowerCase() === 'b2b' && (props.data?.PROVIDER?.UNDER_SANCTION_ENABLED || props.providerData?.UNDER_SANCTION_ENABLED) ? (
                                <GridItem cols={12}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <Switch
                                            value={!!props.editData.UNDER_SANCTION}
                                            onChange={(value) => props.onChange('UNDER_SANCTION', value ? 1 : 0, false)}
                                            disabled={props.disableEdit || props.summary}
                                            label={translate('orderDetail.underSanction')}
                                        />
                                        <Tooltip title={translate('orderDetail.underSanction.tooltip')}>
                                            <InfoIconStyled>
                                                <InfoIcon />
                                            </InfoIconStyled>
                                        </Tooltip>
                                    </div>
                                </GridItem>
                            ) : null}
                        </Grid>
                    </GridItem>
                    <GridItem cols={6}>
                        {!props.newOrder && (
                            <GridItem cols={12}>
                                <DateTimePicker
                                    value={props.editData.TIMESTAMP}
                                    name={'TIMESTAMP'}
                                    label={translate('orderDetail.receivedDate')}
                                    fullWidth
                                    disabled
                                    type="dateTime"
                                />
                            </GridItem>
                        )}
                        {props.editData.ORDER_TYPE?.toLowerCase() === 'b2b' && props.editData.STATUS !== 'preOrder' && (
                            <GridItem cols={12}>
                                <RequiredExpeditionDateField
                                    value={props.editData.REQUIRED_EXPEDITION_DATE}
                                    originalValue={props.data?.REQUIRED_EXPEDITION_DATE}
                                    status={props.editData.STATUS}
                                    disableEdit={props.disableEdit}
                                    summary={props.summary}
                                    error={props.editErrors.REQUIRED_EXPEDITION_DATE}
                                    onChange={(value, error) => props.onChange('REQUIRED_EXPEDITION_DATE', value, error)}
                                />
                            </GridItem>
                        )}
                        <GridItem cols={12}>
                            <AutocompleteTextField
                                value={props.editData.NOTE}
                                error={props.editErrors.NOTE}
                                onChange={(value, error) => props.onChange('NOTE', value || '', error)}
                                name="NOTE"
                                label={translate('orderDetail.note')}
                                fullWidth
                                multiline
                                disabled={props.isAttributeEditDisabled('NOTE') || props.summary}
                                autoComplete="ORDER_NOTE"
                            />
                        </GridItem>
                        <GridItem cols={12}>
                            <TextField
                                value={props.editData.ESHOP_NOTE}
                                error={props.editErrors.ESHOP_NOTE}
                                onChange={(value, error) => props.onChange('ESHOP_NOTE', value, error)}
                                name="ESHOP_NOTE"
                                label={translate('orderDetail.eshopNote')}
                                fullWidth
                                multiline
                                disabled={props.summary}
                            />
                        </GridItem>
                        <GridItem cols={12}>
                            <TextField
                                value={props.editData.MESSAGE_ON_EXPEDITION_BOX}
                                name="MESSAGE_ON_EXPEDITION_BOX"
                                label={translate('orderDetail.messageOnExpeditionBox')}
                                fullWidth
                                multiline
                                disabled
                            />
                        </GridItem>
                    </GridItem>
                    {props.data?.STATUS === 'incorrect' && (
                        <GridItem cols={12}>
                            <TextField
                                value={props.incorrectMessage}
                                label={translate('orderDetail.incorrectMessage')}
                                fullWidth
                                multiline
                                disabled
                                error
                                errorHelperText=" "
                            />
                        </GridItem>
                    )}
                </Grid>
            </GridItem>
        </Grid>
    );
};

export default Detail;
